import classNames from 'classnames';

import React, { forwardRef, ForwardRefExoticComponent, HTMLAttributes, RefAttributes } from 'react';

import styles from './counter.scss';

const MAX_VALUE = 100;

type CounterProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    value?: number | string;
    white?: boolean;
    notEmpty?: boolean;
    withPlusBefore?: boolean;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-counter';

export const Counter: ForwardRefExoticComponent<CounterProps> = forwardRef<
  HTMLDivElement,
  CounterProps
>(
  (
    { value, dataTestId = COMPONENT_ID, white, notEmpty, withPlusBefore, className, ...rest },
    ref,
  ) => {
    const roundedValue =
      value !== undefined
        ? typeof value === 'number'
          ? Math.ceil(Math.max(value, 0))
          : value
        : undefined;

    if (notEmpty && !roundedValue) {
      return null;
    }

    const getFinalValue = () => {
      if (roundedValue >= MAX_VALUE && withPlusBefore) {
        return '+99';
      }

      if (roundedValue >= MAX_VALUE) {
        return '99+';
      }

      if (roundedValue < MAX_VALUE && withPlusBefore) {
        return `+${roundedValue}`;
      }

      if (!notEmpty && roundedValue === 0) {
        return '';
      }

      return roundedValue;
    };

    const isDouble = roundedValue && String(roundedValue).length > 1;

    const classes = classNames(styles.counter, className, {
      [styles.noValue]: !roundedValue,
      [styles.double]: isDouble,
      [styles.withPlusBefore]: withPlusBefore,
      [styles.white]: roundedValue && white,
    });

    return (
      <div className={styles.container} ref={ref}>
        <span
          data-testid={dataTestId}
          data-component-name={COMPONENT_ID}
          data-id={dataTestId}
          className={classes}
          {...rest}>
          {getFinalValue()}
        </span>
      </div>
    );
  },
);

Counter.displayName = 'Counter';
